
import { defineComponent } from "@vue/composition-api";
import { Hotel } from "@/vue-core/models/Hotel.model";
import { ref, onMounted, Ref, reactive, inject, watch, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import RoomFilter from "@/components/hotels/RoomFilter.vue";
import RegisterCard from "@/components/register/RegisterCard.vue";
import { loaderDone, loaderStart } from "@/services/Loader";
import useBreakpoints from "@/services/Breakpoints";
import {
  getRoomsAvailable,
  getRoomsAvailableAcrossDates,
  generateRooms,
  combinations,
  filterCombinationsWithCapacityNeeded,
} from "@/services/RoomFilters";

interface queryRoom {
  query?: Array<{
    id: string;
    roomCapacityAdults: number;
    roomCapacitychildren: number;
  }>;
  pictures?: Array<string | null>;
  price: number;
  roomCapacityAdults: number;
  roomCapacitychildren: number;
  roomName?: string;
  roomType?: string;
}

export default defineComponent({
  components: {
    CoronaRoomFilter: RoomFilter,
    RegisterCard: RegisterCard,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const hotelID = route.params.hotelID;
    const eventID = route.params.eventID;
    const toDate = computed(() => route.query.toDate as string);
    const fromDate = computed(() => route.query.fromDate as string);
    const adultSelected = computed(() => route.query.adultSelected as string);
    const childSelected = computed(() => route.query.childSelected as string);
    const unavailability = ref(false);
    const logged = computed(() => store.getters["user/isLogged"]);
    const { type } = useBreakpoints();
    const hotelDetails: Ref<Hotel | null> = ref(null);
    const roomsData: Array<queryRoom> = reactive([]);
    const roomSelect = ref<queryRoom | null>(null);
    const emitter: any = inject("emitter");
    const toggleRoomFilter = () => {
      emitter.emit("toggleRoomFilter", true);
    };
    const registerOnlineIsEnabled = computed(
      () => store.getters["user/checkIfRegisterOnlineIsEnabled"]
    );
    const activateCardRegister = computed(
      () => store.getters["user/activateCardRegister"]
    );

    onMounted(async () => {
      const hotelData: Hotel = await store.dispatch("user/getHotelDetails", {
        eventID: eventID,
        hotelID: hotelID,
      });
      hotelDetails.value = hotelData;
      searchRooms(hotelData);
    });
    watch([toDate, fromDate, adultSelected, childSelected], () => {
      if (
        hotelDetails.value &&
        toDate.value &&
        fromDate.value &&
        adultSelected.value &&
        childSelected.value
      )
        searchRooms(hotelDetails.value);
    });
    const searchRooms = (hotelData: Hotel) => {
      unavailability.value = false;
      roomsData.splice(0, roomsData.length);
      const adultSelected = route.query.adultSelected;
      const childSelected = route.query.childSelected;
      const rooms: Array<any> = [];
      for (const room of hotelData.rooms!) {
        if (room.availability) {
          rooms.push({
            availability: room.availability,
            roomType: room.roomType,
            id: room.id,
            roomCapacityAdults: room.roomCapacityAdults,
            roomCapacitychildren: room.roomCapacitychildren,
            roomName: room.name,
            price: room.amount,
            picture: room.pictures ? room.pictures[0] : null,
          });
        }
      }
      const checkOut = new Date(toDate.value);
      checkOut.setDate(checkOut.getDate() - 1);
      const checkOutFormat = checkOut.toISOString().split("T")[0];
      const roomsAvailable = getRoomsAvailable(
        fromDate.value,
        checkOutFormat,
        rooms
      );
      if (Object.keys(roomsAvailable).length === 0) {
        unavailability.value = true;
        return;
      }
      const roomsCatalogAvailableOnAllDays =
        getRoomsAvailableAcrossDates(roomsAvailable);

      const roomsGenerated = generateRooms(roomsCatalogAvailableOnAllDays);
      const roomCombinations = combinations(
        roomsGenerated,
        adultSelected,
        childSelected
      );
      const nonRepeatedPermutations = filterCombinationsWithCapacityNeeded(
        adultSelected,
        childSelected,
        roomCombinations
      );
      if (!nonRepeatedPermutations) return;
      nonRepeatedPermutations.sort((a: any, b: any) =>
        a.price > b.price ? 1 : b.price > a.price ? -1 : 0
      );

      for (const room of nonRepeatedPermutations) {
        roomsData.push(room as queryRoom);
      }
    };
    async function checkRegisterWitoutTicket(rooms) {
      roomSelect.value = JSON.parse(JSON.stringify(rooms));
      if (logged.value) return goToReservation(null);
      emitter.emit("toggleDialogRegister", true);
    }
    async function goToReservation(checkIfCompleteRegister) {
      if (checkIfCompleteRegister && activateCardRegister) return;
      loaderStart();
      const rooms = roomSelect.value;
      await store.dispatch("user/addReservation", {
        eventID: route.params.eventID as string,
        hotelID: route.params.hotelID as string,
        rooms: rooms?.query,
        adultSelected: route.query.adultSelected,
        childSelected: route.query.childSelected,
        dates: {
          toDate: route.query.toDate,
          fromDate: route.query.fromDate,
        },
      });
      loaderDone();
    }
    function filterRoomTypes(roomTypes) {
      const roomTypesSplit = roomTypes.split(",");
      let single = 0,
        triple = 0,
        double = 0,
        quad = 0,
        types = "";
      roomTypesSplit.forEach((element) => {
        const type = element.replace(" ", "");
        if (type === "Single") single++;
        if (type === "Double") double++;
        if (type === "Triple") triple++;
        if (type === "Quad") quad++;
      });
      if (single) types += "Single X" + single;
      if (single && double) types += ", ";
      if (double) types += "Double X" + double;
      if (double && triple) types += ", ";
      if (triple) types += "Triple X" + triple;
      if (triple && quad) types += ", ";
      if (quad) types += "Quad X" + quad;
      return types;
    }
    const filterBodyContainerClasses = computed(() => {
      if (type.value === "sm") return "body-container-xs";
      return "body-container-xl";
    });
    const filterOverflowHiddenClasses = computed(() => {
      if (type.value === "sm") return "overflow-hidden-xs";
      return "overflow-hidden-xl";
    });
    const filerRoomFilterClasses = computed(() => {
      if (type.value === "sm") return "room-filter-container-xs";
      return "room-filter-container-xl";
    });
    return {
      toDate,
      fromDate,
      hotelDetails,
      roomsData,
      unavailability,
      toggleRoomFilter,
      goToReservation,
      filterRoomTypes,
      checkRegisterWitoutTicket,
      filterBodyContainerClasses,
      filterOverflowHiddenClasses,
      filerRoomFilterClasses,
    };
  },
});
